const LOADER_SCRIPT_ID = "vzc-agent-core-chat-loader";

const getLoader = () => window.__vzcChatLoader;

const getConfigKey = (config) =>
  [config?.endpoint, config?.authHost, config?.authClientId, config?.loaderUrl, config?.scriptUrl].join("|");

const resolveLoader = (script, resolve, reject) => {
  const loader = getLoader();
  if (loader) {
    resolve(loader);
    return;
  }
  script.remove();
  reject(
    new Error(
      `AgentCore chat loader did not initialize. Check that ${script.src} serves the loader bundle and not an HTML fallback.`,
    ),
  );
};

const waitForLoaderScript = (script) =>
  new Promise((resolve, reject) => {
    if (getLoader()) {
      resolve(getLoader());
      return;
    }

    const handleLoad = () => resolveLoader(script, resolve, reject);
    const handleError = () => {
      script.remove();
      reject(new Error(`Unable to load the AgentCore chat loader from ${script.src}`));
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
  });

const createLoaderPromise = (loaderUrl) => {
  if (getLoader()) {
    return Promise.resolve(getLoader());
  }

  const existingScript = document.getElementById(LOADER_SCRIPT_ID);
  if (existingScript) {
    return waitForLoaderScript(existingScript);
  }

  const script = document.createElement("script");
  script.id = LOADER_SCRIPT_ID;
  script.src = loaderUrl;
  script.defer = true;
  const loaderReady = waitForLoaderScript(script);
  document.head.appendChild(script);
  return loaderReady;
};

export class AgentCoreChatService {
  constructor() {
    this.loaderPromise = undefined;
    this.initializedConfigKey = null;
    this.sessionId = 0;
  }

  async ensureLoader(loaderUrl) {
    if (!this.loaderPromise) {
      this.loaderPromise = createLoaderPromise(loaderUrl).catch((error) => {
        this.loaderPromise = undefined;
        throw error;
      });
    }
    return this.loaderPromise;
  }

  async initializeWidget(config) {
    const sessionId = this.sessionId;
    const loader = await this.ensureLoader(config.loaderUrl);

    if (sessionId !== this.sessionId) {
      return;
    }

    const nextConfigKey = getConfigKey(config);
    if (this.initializedConfigKey !== nextConfigKey) {
      if (this.initializedConfigKey) {
        loader.destroy();
      }
      loader.init({
        endpoint: config.endpoint,
        authHost: config.authHost,
        authClientId: config.authClientId,
        scriptUrl: config.scriptUrl,
      });
      this.initializedConfigKey = nextConfigKey;
    }

    if (sessionId !== this.sessionId) {
      return;
    }

    loader.load();
  }

  showWidget() {
    getLoader()?.load();
  }

  hideWidget() {
    getLoader()?.close();
  }

  destroyWidget() {
    this.sessionId += 1;
    getLoader()?.destroy();
    this.initializedConfigKey = null;
  }
}

export const createAgentCoreChatService = () => new AgentCoreChatService();

const defaultChatService = createAgentCoreChatService();

export const validateConfig = (config) => {
  // Auth0 is required by the widget; getAgentCoreChatConfig already omits envs
  // that do not yet have authHost/authClientId.
  return Boolean(config?.endpoint && config.authHost && config.authClientId && config.loaderUrl && config.scriptUrl);
};

export const initializeWidget = (config) => defaultChatService.initializeWidget(config);

export const showWidget = () => defaultChatService.showWidget();

export const hideWidget = () => defaultChatService.hideWidget();

export const destroyWidget = () => defaultChatService.destroyWidget();
